import React from 'react';
import { createLDReactProvider } from '@launchdarkly/react-sdk';
import { getLDClientId, getLDEnv, LD_FALLBACK_CLIENT_ID } from './ldConfig';
import { buildAnonymousLDContext } from './buildLDContext';

/**
 * Standing rules — do not violate:
 *
 * 1. This provider must ALWAYS be mounted, unconditionally. Never gate it
 *    behind an `if (isLDConfigured())`-style check, even when no
 *    REACT_APP_LD_CLIENT_ID is configured for the current environment. The
 *    fallback client ID (`LD_FALLBACK_CLIENT_ID`) keeps the SDK inert (it
 *    never calls start() because `deferInitialization: true` is set and
 *    nothing triggers it) rather than the provider being skipped outright —
 *    whether the SDK's typed flag hooks behave safely with zero ancestor
 *    provider mounted has never been confirmed, so this inert-but-present
 *    shape is the permanent design, not a placeholder.
 * 2. `useFlags()` from `@launchdarkly/react-sdk` must never be imported
 *    anywhere in this codebase — it is deprecated.
 * 3. This component wraps only the LD SDK provider. It does not mount
 *    `LDAuthSync` — `LDAuthSync` needs `useSelector` (Redux), so it must be
 *    mounted inside `<Provider store={Store}>` in src/index.js, not here.
 */
const LDReactProvider = createLDReactProvider(
  getLDClientId() || LD_FALLBACK_CLIENT_ID,
  buildAnonymousLDContext(getLDEnv()),
  { deferInitialization: true }
);

export default function LaunchDarklyProvider({ children }) {
  return (
    <LDReactProvider>
      {children}
    </LDReactProvider>
  );
}
